// @ts-nocheck
// src/components/PendingCheckinsBar.tsx
// ---------------------------------------------------------------------------
// v6.478 — ΜΟΝΙΜΗ ΚΟΚΚΙΝΗ ΜΠΑΡΑ ΕΚΚΡΕΜΩΝ CHECK-IN / CHECK-OUT
//
// ΕΝΑ tablet, πολλά σκάφη: ο υπάλληλος αφήνει ένα check-in στη μέση, πάει σε
// άλλο σκάφος και ΓΥΡΝΑΕΙ να το τελειώσει. Η μπάρα:
//   - φαίνεται στην ΚΟΡΥΦΗ ΚΑΘΕ οθόνης
//   - ΔΕΝ κλείνει, ΔΕΝ κρύβεται, ΔΕΝ φεύγει με refresh
//   - φεύγει ΜΟΝΟ όταν το check-in υποβληθεί (ο SERVER το κρίνει)
//
// Ο SERVER είναι ο ΜΟΝΟΣ κριτής (api/pending-checkins.php):
//   pending = υπάρχουν ΗΔΗ γραμμές στη βάση ΚΑΙ δεν έγινε submit.
//   scope=staff → μόνο ΖΩΝΤΑΝΟΙ ναύλοι. Οι παλιοί/τελειωμένοι ΔΕΝ μπαίνουν εδώ
//   (φαίνονται μόνο στην κάρτα του Admin, scope=admin).
// ---------------------------------------------------------------------------

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DataContext } from '../App';
import authService from '../authService';

const API_BASE = 'https://yachtmanagementsuite.com/api';
const POLL_MS = 60 * 1000;

// Ρόλοι που βλέπουν τη μπάρα. Ο ΠΕΛΑΤΗΣ (χωρίς login) ΔΕΝ τη βλέπει ποτέ.
const STAFF_ROLES = ['ADMIN', 'TECHNICAL', 'BOOKING', 'ACCOUNTING', 'FINANCIAL'];

const T = {
  el: {
    one: 'εκκρεμές check-in',
    many: 'εκκρεμή check-in',
    title: 'ΕΚΚΡΕΜΗ CHECK-IN',
    resume: 'Συνέχισε',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    idleFor: 'Στάσιμο εδώ και',
    min: 'λεπτά',
    hours: 'ώρες',
    days: 'μέρες',
    items: 'στοιχεία',
    stoppedAt: 'Σταμάτησε στη',
    page: 'Σελίδα',
    note: 'Δεν κλείνει. Φεύγει μόνο με ολοκληρωμένο Submit.',
    tapToOpen: 'πάτα για λίστα',
  },
  en: {
    one: 'pending check-in',
    many: 'pending check-ins',
    title: 'PENDING CHECK-INS',
    resume: 'Resume',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    idleFor: 'Idle for',
    min: 'min',
    hours: 'hours',
    days: 'days',
    items: 'items',
    stoppedAt: 'Stopped at',
    page: 'Page',
    note: 'Cannot be dismissed. It disappears only after a completed Submit.',
    tapToOpen: 'tap for list',
  },
};

function ageText(minutes, t) {
  if (minutes == null) return '—';
  if (minutes < 90) return `${minutes} ${t.min}`;
  if (minutes < 60 * 48) return `${Math.round(minutes / 60)} ${t.hours}`;
  return `${Math.round(minutes / (60 * 24))} ${t.days}`;
}

export default function PendingCheckinsBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const ctx = useContext(DataContext);

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(() => {
    try { return sessionStorage.getItem('yacht_lang') || 'en'; } catch (e) { return 'en'; }
  });

  const t = T[lang === 'el' ? 'el' : 'en'];

  // Ο πελάτης δεν τη βλέπει — μόνο staff.
  const user = authService.getCurrentUser && authService.getCurrentUser();
  const isStaff = !!user && STAFF_ROLES.includes(String(user.role || '').toUpperCase());

  const load = useCallback(async () => {
    if (!isStaff) { setItems([]); return; }
    try {
      const r = await fetch(`${API_BASE}/pending-checkins.php?scope=staff&t=${Date.now()}`);
      if (!r.ok) return;                    // network hiccup: κράτα την προηγούμενη λίστα
      const d = await r.json();
      if (d && d.success && Array.isArray(d.pending)) setItems(d.pending);
    } catch (e) {
      // ΔΕΝ σβήνουμε τη λίστα σε σφάλμα δικτύου — η μπάρα δεν εξαφανίζεται σιωπηλά.
      console.warn('PendingCheckinsBar: fetch failed (keeping last list):', e);
    }
  }, [isStaff]);

  useEffect(() => { load(); }, [load, location.pathname]);

  useEffect(() => {
    const id = setInterval(load, POLL_MS);
    const onFocus = () => load();
    const onChanged = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('pendingCheckinsChanged', onChanged);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pendingCheckinsChanged', onChanged);
    };
  }, [load]);

  // Γλώσσα: το sessionStorage αλλάζει από τα TopControls κάθε σελίδας.
  useEffect(() => {
    const id = setInterval(() => {
      try {
        const l = sessionStorage.getItem('yacht_lang') || 'en';
        setLang((prev) => (prev === l ? prev : l));
      } catch (e) { /* ignore */ }
    }, 2000);
    return () => clearInterval(id);
  }, []);

  const resume = (p) => {
    // Φόρτωσε ΤΟΝ ΣΥΓΚΕΚΡΙΜΕΝΟ ναύλο + mode και πήγαινε ΕΚΕΙ ΠΟΥ ΣΤΑΜΑΤΗΣΕ.
    try {
      localStorage.setItem('currentBooking', p.bookingNumber);
      localStorage.setItem('currentMode', p.mode);
      sessionStorage.setItem('currentBooking', p.bookingNumber);
      // Ο server έκρινε ότι είναι εκκρεμές → σημάδι για τα date guards της σελ.1.
      sessionStorage.setItem('resumePending', JSON.stringify({
        bookingNumber: p.bookingNumber, mode: p.mode, at: Date.now(),
      }));
    } catch (e) { /* ignore */ }

    if (ctx && typeof ctx.updateData === 'function') {
      ctx.updateData({ bookingNumber: p.bookingNumber, mode: p.mode });
    }
    window.dispatchEvent(new CustomEvent('bookingChanged', { detail: { bookingNumber: p.bookingNumber } }));
    window.dispatchEvent(new CustomEvent('modeChanged', { detail: { mode: p.mode } }));

    setOpen(false);
    navigate(`/page${p.resumePage || 4}`);
  };

  if (!isStaff || items.length === 0) return null;

  const n = items.length;

  return (
    <>
      {/* spacer ώστε η fixed μπάρα να μην καλύπτει περιεχόμενο */}
      <div style={{ height: 38 }} aria-hidden="true" />

      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
          background: '#dc2626', color: '#fff',
          boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
          fontFamily: 'inherit',
        }}
      >
        <button
          onClick={() => setOpen((o) => !o)}
          style={{
            width: '100%', height: 38, border: 'none', background: 'transparent',
            color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            letterSpacing: 0.3,
          }}
        >
          <span>⚠️ {n} {n === 1 ? t.one : t.many}</span>
          <span style={{ fontWeight: 500, opacity: 0.85, fontSize: 12 }}>
            ({t.tapToOpen}) {open ? '▲' : '▼'}
          </span>
        </button>

        {open && (
          <div style={{ background: '#7f1d1d', maxHeight: '70vh', overflowY: 'auto', padding: '8px 8px 12px' }}>
            <div style={{ fontSize: 12, opacity: 0.9, padding: '4px 6px 8px', fontWeight: 700 }}>
              {t.title}
            </div>

            {items.map((p) => (
              <div
                key={`${p.bookingNumber}|${p.mode}`}
                style={{
                  background: '#fff', color: '#111827', borderRadius: 10,
                  padding: '10px 12px', marginBottom: 8,
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 14 }}>
                    {p.bookingNumber}
                    <span style={{
                      marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 6px',
                      borderRadius: 6, background: p.mode === 'in' ? '#dbeafe' : '#fce7f3',
                      color: p.mode === 'in' ? '#1e40af' : '#9d174d',
                    }}>
                      {p.mode === 'in' ? t.checkIn : t.checkOut}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>
                    ⛵ {p.vesselName || '—'} · {t.idleFor} {ageText(p.ageMinutes, t)}
                  </div>
                  <div style={{ fontSize: 12, color: '#b91c1c', fontWeight: 700, marginTop: 2 }}>
                    {p.doneItems}/{p.totalItems} {t.items} · {t.stoppedAt} {t.page} {p.resumePage}
                  </div>
                </div>

                <button
                  onClick={() => resume(p)}
                  style={{
                    background: '#16a34a', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '10px 18px', fontWeight: 800,
                    fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  ▶ {t.resume}
                </button>
              </div>
            ))}

            <div style={{ fontSize: 11, opacity: 0.85, padding: '2px 6px' }}>
              {t.note}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
